import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useMapsLibrary } from '@vis.gl/react-google-maps';
import PropTypes from 'prop-types';
import { AddLocation } from 'registration/components/new_pages/OnboardingLocationPersonalized/AddLocation';
import {
  getDesiredLocationValuesByState,
  getJobCountText,
} from 'registration/components/new_pages/OnboardingLocationPersonalized/helpers';
import { LocationTags } from 'registration/components/new_pages/OnboardingLocationPersonalized/LocationTags';
import { OnboardingTitle } from 'registration/components/new_pages/OnboardingLocationPersonalized/OnboardingTitle';
import { desiredLocationOptionsFrozen } from 'registration/constants/desiredLocationOptions';
import {
  byOpenings,
  bySalary,
} from 'registration/constants/personalizedLocationDefaults';
import {
  Onboarding,
  OnboardingContext,
} from 'registration/containers/Onboarding';
import {
  ensureDesiredLocationsArray,
  ensureDesiredLocationsTagCompatible,
} from 'registration/helpers/desiredLocations';
import {
  DELIMITER,
  getShouldPreselectLocations,
  getUnbounceParamFromLocalforage,
  UnbounceParam,
} from 'registration/helpers/experiment/xena';
import getNurseSpecialtyIds from 'registration/helpers/getNurseSpecialtyIds';
import getSalaryText from 'registration/helpers/getSalaryText';
import normalizeLocation from 'registration/helpers/normalizeLocation';
import normalizeMarketName from 'registration/helpers/normalizeMarketName';
import { locationFromGooglePlace } from 'registration/helpers/talentProfileConversions';
import { DesiredLocation } from 'registration/types';

import { ButtonSelect } from 'shared/components/ihcl/button_select';
import { Center } from 'shared/components/ihcl/positioning';
import { styled } from 'shared/components/ihcl/styled';
import { Button } from 'shared/components/ihcl/v2/button';
import { Spinner } from 'shared/components/ihcl/v2/spinner';
import { pathToVariant } from 'shared/helpers/activateABTest';
import { doPost } from 'shared/helpers/http';
import simplePluralize from 'shared/helpers/simplePluralize';

const filteredLocationValues = [
  ...desiredLocationOptionsFrozen.filter((dl) =>
    Object.prototype.hasOwnProperty.call(dl, 'value')
  ),
];

const desiredLocationStateNameMap = desiredLocationOptionsFrozen
  .filter((dl) => !Object.prototype.hasOwnProperty.call(dl, 'value'))
  .reduce(
    (stateMap, state) => ({
      ...stateMap,
      [state.section.toUpperCase()]: state.label,
    }),
    {}
  );

const { desiredLocationValuesByState } = getDesiredLocationValuesByState({
  filteredLocationValues,
  desiredLocationStateNameMap,
});

const SpinnerContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  marginTop: '50px',
  marginBottom: '50px',
});

const SelectedLocations = styled('div', {
  marginTop: '1em',
});

const getNewLocations = async (onboardingInformation) =>
  doPost('/talent_onboard/custom_job_openings', onboardingInformation)
    .then((resp) => {
      if (resp && resp.length) {
        return resp.map(normalizeLocation);
      }
      throw new Error('No personalized job matches.');
    })
    .catch((err) => {
      console.error(err);
      if (
        onboardingInformation &&
        onboardingInformation.locationMotivation &&
        onboardingInformation.locationMotivation.includes('compensation') &&
        !onboardingInformation.locationMotivation.includes(
          'placement_potential'
        )
      ) {
        return bySalary;
      }
      return byOpenings;
    });

export const OnboardingLocationPersonalized = ({
  nextAction = null,
  previousAction = null,
  stepNumber,
}) => {
  const {
    onboardingBasePath,
    onboardingData,
    onboardingUpdates,
    setOnboardingData,
  } = useContext(OnboardingContext);
  const { desiredLocations, nurseSpecialties } = onboardingData;
  const selectedLocations =
    ensureDesiredLocationsTagCompatible(desiredLocations);
  const {
    education_level: educationLevel,
    location_motivation: locationMotivation,
    nurse_specialties_attributes: nurseSpecialtiesAttributes,
  } = onboardingUpdates;
  const emptySelectedLocations =
    !selectedLocations || (selectedLocations && selectedLocations.length === 0);
  const { profileType = 'talent' } = useContext(OnboardingContext);
  const [newLocations, setNewLocations] = useState([]);
  const [showTenLocations, setShowTenLocations] = useState(false);
  const [paramLocations, setParamLocations] = useState(null);

  const [shouldPreselectOtherLocations, setShouldPreselectOtherLocations] =
    useState(false);

  const variant = pathToVariant(onboardingBasePath);

  useEffect(() => {
    getShouldPreselectLocations(variant).then((value) =>
      setShouldPreselectOtherLocations(value)
    );
  }, [variant]);

  useEffect(() => {
    if (shouldPreselectOtherLocations) {
      // also get any locations we saved from unbound query param
      const getParamLocation = async () => {
        const paramValue = await getUnbounceParamFromLocalforage(
          UnbounceParam.desired_location
        );

        setParamLocations(paramValue);
      };

      getParamLocation();
    }
  });

  const placesLib = useMapsLibrary('places');
  const autocompleteService = useMemo(
    () => placesLib && new placesLib.AutocompleteService(),
    [placesLib]
  );

  const getResultsFromGoogle = async (input) => {
    const resp = await autocompleteService?.getPlacePredictions({
      input,
      componentRestrictions: { country: 'us' },
      types: ['(cities)'],
    });

    return resp?.predictions;
  };

  const addTag = useCallback(
    (tagObj) => {
      setOnboardingData((data) => {
        if (!data.desiredLocations) {
          return {
            ...data,
            desiredLocations: [tagObj],
          };
        }
        if (
          data.desiredLocations.every((location) => location.id !== tagObj.id)
          // using data.desiredLocations instead of selectedLocations here to
          // eliminate extra dependency and excessive firing of below effect
        ) {
          return {
            ...data,
            desiredLocations: [
              ...ensureDesiredLocationsArray(data.desiredLocations),
              tagObj,
            ],
          };
        }

        return data;
      });
    },
    [setOnboardingData]
  );

  useEffect(() => {
    const addTagsFromGoogleMatches = async () => {
      paramLocations?.split(DELIMITER).forEach(async (paramLocation) => {
        if (paramLocation.trim()) {
          const results = await getResultsFromGoogle(paramLocation);

          if (results?.length > 0) {
            const locationValues = results.map(locationFromGooglePlace) as {
              city: any;
              id: any;
              label: string;
              state: any;
            }[];
            const match = locationValues.find(
              (locationValue) =>
                locationValue?.label.replace(/ +/g, '').toLowerCase() ===
                paramLocation.replace(/ +/g, '').toLowerCase()
            );
            if (match) addTag(match);
          }
        }
      });
    };

    addTagsFromGoogleMatches();
  }, [paramLocations, addTag, autocompleteService]);

  useEffect(() => {
    const fetchNewLocations = async () => {
      const customNewLocations = await getNewLocations({
        educationLevel,
        locationMotivation,
        nurseSpecialties: [...getNurseSpecialtyIds(nurseSpecialties)].concat(
          nurseSpecialtiesAttributes?.map(
            (nsa) => nsa.nurse_specialty_description_id
          ) || []
        ),
      });

      setNewLocations(customNewLocations);
    };

    if (profileType !== 'tech') {
      fetchNewLocations();
    }
  }, [
    educationLevel,
    locationMotivation,
    nurseSpecialties,
    nurseSpecialtiesAttributes,
    profileType,
  ]);

  const removeTag = ({ id }) => {
    const filteredTags = selectedLocations.filter((tag) => tag.id !== id);
    setOnboardingData({
      ...onboardingData,
      desiredLocations: filteredTags,
    });
  };

  const locationOptions =
    newLocations && newLocations.length
      ? newLocations.map((newLocation) => ({
          ...newLocation,
          label: (
            <>
              <strong>{normalizeMarketName(newLocation.market)}</strong>
              {!!newLocation.jobCount &&
                `${getJobCountText(newLocation.jobCount)}, `}
              {getSalaryText(newLocation.maxSalary)}
            </>
          ),
          value: newLocation.market,
          tagHidden: true,
        }))
      : [];
  const shownLocations = showTenLocations
    ? locationOptions
    : locationOptions.slice(0, 3);
  const getSelectedOptions = (selectedOptions) =>
    shownLocations.filter((selectedOption) =>
      selectedOptions.some((option) => option.value === selectedOption.value)
    );

  const tagFilteredSelectedOptions = selectedLocations?.filter(
    (selectedLocation) => !selectedLocation.tagHidden
  );

  const specialtyText = !nurseSpecialties
    ? 'specialty'
    : simplePluralize(nurseSpecialties.size, 'specialty', 'specialties', false);

  return (
    <Onboarding
      currentStep={stepNumber}
      isNextDisabled={emptySelectedLocations}
      nextAction={nextAction}
      previousAction={previousAction}
      stepKeys={['desiredLocations']}
      stepName="Additional locations"
    >
      {!(newLocations && newLocations.length) && profileType !== 'tech' && (
        <OnboardingTitle>
          Searching for matching jobs
          <SpinnerContainer>
            <Spinner className="size-8" />
          </SpinnerContainer>
        </OnboardingTitle>
      )}
      {((newLocations && newLocations.length > 0) ||
        profileType === 'tech') && (
        <>
          <OnboardingTitle>Where would you like to work?</OnboardingTitle>
          <SelectedLocations>
            {tagFilteredSelectedOptions && (
              <LocationTags
                locations={tagFilteredSelectedOptions}
                removeTag={removeTag}
              />
            )}
            <AddLocation
              addTag={(newTag) => addTag(newTag)}
              defaultOptions={desiredLocationValuesByState}
              getResultsFromGoogle={getResultsFromGoogle}
            />
          </SelectedLocations>
          {locationOptions?.length > 0 && (
            <Center>
              <span style={{ fontWeight: 900 }}>
                Popular areas for your {specialtyText}
              </span>
              <p>Tap to add areas you are interested in.</p>
              <ButtonSelect
                buttonProps={{
                  className: 'flex flex-col rounded-[52px]',
                }}
                fillWidth
                multiSelect
                onChange={(val, option, remove) =>
                  remove
                    ? setOnboardingData({
                        ...onboardingData,
                        desiredLocations: selectedLocations.filter(
                          (selectedLocation) =>
                            selectedLocation.value !== option.value
                        ),
                      })
                    : setOnboardingData({
                        ...onboardingData,
                        desiredLocations: [
                          ...selectedLocations,
                          option as DesiredLocation,
                        ],
                      })
                }
                selectedOptions={getSelectedOptions(selectedLocations)}
                options={shownLocations}
              />
              {!showTenLocations &&
                locationOptions.length > shownLocations.length && (
                  <>
                    <br />
                    <Button
                      variant="minimal"
                      onClick={() => setShowTenLocations(true)}
                    >
                      Show more
                    </Button>
                  </>
                )}
            </Center>
          )}
        </>
      )}
    </Onboarding>
  );
};

OnboardingLocationPersonalized.propTypes = {
  nextAction: PropTypes.string,
  previousAction: PropTypes.string,
  stepNumber: PropTypes.number.isRequired,
};
